import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import "./GoogleSignInPanel.css";

const GOOGLE_SCRIPT_ID = "google-identity-services";
const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Browser environment is unavailable."));
      return;
    }

    const existing = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existing && window.google?.accounts?.id) {
      resolve(window.google);
      return;
    }

    if (existing) {
      existing.addEventListener("load", () => resolve(window.google), { once: true });
      existing.addEventListener("error", () => reject(new Error("Google script failed to load.")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error("Google script failed to load."));
    document.body.appendChild(script);
  });
}

const GoogleSignInPanel = () => {
  const buttonRef = useRef(null);
  const [loadError, setLoadError] = useState("");
  const {
    authMode,
    authError,
    googleClientId,
    isAuthenticating,
    signInWithGoogleCredential,
    signInWithMock,
  } = useAuth();

  useEffect(() => {
    const buttonElement = buttonRef.current;

    if (authMode !== "google" || !googleClientId || !buttonElement) {
      return undefined;
    }

    let cancelled = false;
    buttonElement.innerHTML = "";
    setLoadError("");

    loadGoogleScript()
      .then((google) => {
        if (cancelled || !buttonElement || !google?.accounts?.id) return;

        google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (response) => {
            if (!response?.credential) return;
            try {
              await signInWithGoogleCredential(response.credential);
            } catch (error) {
              console.error("Google sign-in failed.", error);
            }
          },
        });

        google.accounts.id.renderButton(buttonElement, {
          theme: "outline",
          size: "large",
          shape: "rectangular",
          text: "signin_with",
          logo_alignment: "left",
          width: 260,
        });
      })
      .catch((error) => {
        setLoadError(error.message);
      });

    return () => {
      cancelled = true;
      if (buttonElement) {
        buttonElement.innerHTML = "";
      }
    };
  }, [authMode, googleClientId, signInWithGoogleCredential]);

  if (authMode === "mock") {
    return (
      <div className="google-signin-panel">
        <button className="google-mock-button" onClick={signInWithMock}>
          ローカル確認用ログイン
        </button>
        <p className="google-signin-note">
          localhost ではモック認証で保存フローを確認できます。
        </p>
      </div>
    );
  }

  if (authMode === "disabled") {
    return (
      <div className="google-signin-panel">
        <p className="google-signin-error">
          Google ログインの設定がまだありません。`REACT_APP_GOOGLE_CLIENT_ID` を追加してください。
        </p>
      </div>
    );
  }

  return (
    <div className="google-signin-panel">
      <div ref={buttonRef} />
      {isAuthenticating ? (
        <p className="google-signin-note">ログイン処理中です...</p>
      ) : null}
      {loadError || authError ? (
        <p className="google-signin-error">{loadError || authError}</p>
      ) : null}
    </div>
  );
};

export default GoogleSignInPanel;
